<script setup>
import {ref, watch} from "vue";
import {useStore} from "vue2-helpers/vuex";
import userService from "@/services/user.service";

const showModal = ref(false);

const store = useStore();

watch(() => store.state.user, (user) => {
    if ('summit_popup_shown_at' in user && !user.summit_popup_shown_at) {
        showModal.value = true;
    }
});

watch(showModal, () => {
    if (!showModal.value) {
        userService.hideSummitPopup();
    }
});

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" modal-class="sp-popup" hide-footer hide-header>
        <div class="sp-popup__header">
            <button
                class="sp-popup__close"
                aria-label="Close"
                @click="showModal = false"
            >
                ✕
            </button>
            <div class="sp-popup__eyebrow">Live Event</div>
            <h2 class="sp-popup__title" id="popup-title">
                Join the SwitchPitch Summit<br><span>– April 29 in NYC</span>
            </h2>
            <p class="sp-popup__subtitle modal-text">
                Connect with executives from BNY, Mastercard, Colgate, Continental, Southwest, and more.
            </p>
        </div>

        <div class="sp-popup__body">

            <div class="sp-popup__benefits-label">What's in it for you</div>
            <ul class="sp-popup__benefits">
                <li>Discover new ways to source startups</li>
                <li>Learn how peers are scaling partnerships</li>
                <li>Build relationships with fellow innovation leaders</li>
            </ul>

            <div class="sp-popup__meta">
                <div class="sp-popup__meta-icon">📍</div>
                <div class="sp-popup__meta-text">
                    April 29, 2026
                    <span>BNY Headquarters, New York City</span>
                </div>
            </div>

            <a class="sp-popup__cta" target="_blank" href="https://luma.com/fxmy9j5g">
                Reserve your spot
                <span class="sp-popup__cta-badge">Code: SP50</span>
                →
            </a>

            <p class="sp-popup__footer modal-text">Use code SP50 at checkout for a discounted registration.</p>
        </div>
    </b-modal>
</template>

<style scoped>
.sp-popup {
    background: #fff;
    border-radius: 16px;
    width: 100%;
    max-width: 480px;
    overflow: hidden;
    box-shadow: 0 24px 60px rgba(4, 54, 72, 0.28);
    animation: popIn .28s cubic-bezier(.34, 1.56, .64, 1) both;
    position: relative;
}

.sp-popup__header {
    border-radius: 8px 8px 0 0;
    background: linear-gradient(135deg, #043648 0%, #03465e 60%, #005f85 100%);
    padding: 28px 28px 24px;
    position: relative;
}

.sp-popup__close {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 30px;
    height: 30px;
    background: rgba(255, 255, 255, .12);
    border: none;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, .7);
    font-size: 16px;
    transition: background .2s, color .2s;
}

.sp-popup__eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(0, 175, 239, .18);
    border: 1px solid rgba(0, 175, 239, .35);
    color: #00afef;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 20px;
    margin-bottom: 12px;
}

.sp-popup__title {
    color: #fff;
    font-size: 20px;
    font-weight: 800;
    line-height: 1.3;
    margin-bottom: 4px;
}

.sp-popup__subtitle {
    color: rgba(255, 255, 255, .72);
    font-size: 12px;
    font-weight: 500;
    line-height: 1.5;
    margin-top: 8px;
}

.sp-popup__body {
    padding: 24px 28px;
}

.sp-popup__benefits-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .1em;
    color: #043648;
    margin-bottom: 10px;
}

.sp-popup__benefits {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;

    li {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        font-size: 12.5px;
        font-weight: 500;
        color: #3b3b3b;
        line-height: 1.5;
    }
}

.sp-popup__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #ebf7fb;
    border-left: 3px solid #00afef;
    border-radius: 0 8px 8px 0;
    padding: 10px 14px;
    margin-bottom: 20px;
}

.sp-popup__meta-icon {
    font-size: 18px;
}

.sp-popup__meta-text {
    font-size: 12px;
    font-weight: 700;
    color: #043648;
    line-height: 1.4;

    span {
        display: block;
        font-weight: 500;
        color: #5d5d5d;
        font-size: 11px;
    }
}

.sp-popup__cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px 20px;
    background: linear-gradient(90deg, #00afef, #0083b2);
    color: #fff;
    font-family: 'Montserrat', sans-serif;
    font-size: 13px;
    font-weight: 700;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: filter .2s, transform .15s;
    text-decoration: none;

    .sp-popup__cta-badge {
        background: rgba(255, 255, 255, .22);
        border-radius: 4px;
        padding: 2px 7px;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: .04em;
    }
}

.sp-popup__footer {
    text-align: center;
    color: #949494;
    font-size: 10.5px;
    font-weight: 500;
    margin-top: 12px;
}

</style>
